export const isValidUrl = (string: string) => {
  let url;
  try {
    url = new URL(string);
  } catch {
    return false;
  }
  return url.protocol === "http:" || url.protocol === "https:";
};

export const isValidSha256Hash = (hash: string): boolean => {
  // SHA-256 hash is 64 hexadecimal characters
  return /^[a-f0-9]{64}$/i.test(hash);
};

export const isValidEmail = (email: string): boolean => {
  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const hashEmail = async (email: string): Promise<string> => {
  if (!email) return "";
  const normalizedString = email.toLowerCase().trim();
  const msgBuffer = new TextEncoder().encode(normalizedString);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};

export const generateRandomString = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

export const ensurePresent = async <T>(
  predicate: () => T | null | undefined,
  timeout: number,
  pollingInterval: number = 100,
): Promise<T | null> => {
  const currentValue = predicate();
  if (currentValue !== undefined && currentValue !== null) {
    return currentValue;
  }
  let currentTimeout = 0;
  // At some point we would like to have progressive interval, meaning
  // the longer you wait the longer the interval becomes.
  const effectivePollingInterval = Math.min(timeout, pollingInterval);
  return new Promise<T | null>((resolve) => {
    const intervalId = setInterval(() => {
      currentTimeout += effectivePollingInterval;
      const value = predicate();
      if (value !== undefined && value !== null) {
        clearInterval(intervalId);
        resolve(value);
      }
      if (currentTimeout >= timeout) {
        clearInterval(intervalId);
        resolve(null);
      }
    }, effectivePollingInterval);
  });
};

/**
 * Merge configuration objects, filtering out undefined values from overrides
 * @param defaults - Default configuration values
 * @param overrides - Override configuration values (undefined values are ignored)
 * @returns Merged configuration with defaults and defined overrides
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeConfig = <T extends Record<string, any>>(
  defaults: T,
  overrides: Partial<T>,
): T => {
  // Filter out undefined values from overrides
  const definedOverrides = Object.fromEntries(
    Object.entries(overrides).filter(([_, value]) => value !== undefined),
  ) as Partial<T>;

  return {
    ...defaults,
    ...definedOverrides,
  };
};
